import { ChangeDetectorRef, Injectable } from '@angular/core';
import {
  ExportPdfDocument,
  PdfExportItem,
} from '@common-modules/shared/exports/models/export-pdf-document';
import { GenericExportService } from '@common-modules/shared/exports/service/generic-export.service';
import { globalUtilsHelper } from '@common-modules/shared/helpers/global-utils-helper';
import { ECharts } from 'echarts';
import { ZRColor } from 'echarts/types/dist/shared';
import { BehaviorSubject, ReplaySubject, Subject } from 'rxjs';
import { GenericChartSettings } from '../models/generic-chart-settings/generic-chart-settings';
import { EchartsSettingsMapperService } from '../services/echarts-settings-mapper.service';

export enum ChartExportType {
  PDF = 'pdf',
  PNG = 'png',
  Base64 = 'base64',
}

@Injectable()
export class GenericChartExportService {
  private readonly _nativeExportSettings$ = new BehaviorSubject(null);
  readonly nativeExportSettings$ = this._nativeExportSettings$.asObservable();

  private readonly _chartObjectToExport$ = new Subject<string>();
  readonly chartObjectToExport$ = this._chartObjectToExport$.asObservable();

  private readonly _enableExportChart$ = new ReplaySubject<boolean>(1);
  readonly enableExportChart$ = this._enableExportChart$.asObservable();

  private readonly _loading$ = new ReplaySubject<boolean>(1);
  readonly loading$ = this._loading$.asObservable();

  private _chartInstanceExport: ECharts;
  private readonly _genericFilename = 'chart';
  private readonly _pdfImageXCoords = 20;
  private readonly _pdfImageWidth = 240;
  private _settings: GenericChartSettings;
  private _exportEnqueued = false;
  private _exportType: ChartExportType | null = null;
  private _cd: ChangeDetectorRef;

  constructor(
    private readonly _mapper: EchartsSettingsMapperService,
    private readonly _genericExportService: GenericExportService
  ) {}

  setChangeDetector = (cd: ChangeDetectorRef) => (this._cd = cd);

  setChartSettings = (settings: GenericChartSettings) => (this._settings = settings);

  startExport(exportType: ChartExportType): void {
    this._enableExportChart$.next(true);
    this._loading$.next(true);
    this._exportType = exportType;
    this.buildExportChart();
    this._cd.detectChanges();
  }

  onExportChartInit = (instance: ECharts) => (this._chartInstanceExport = instance);

  onExportChartStable(event): void {
    if (!this._exportEnqueued) {
      this._exportEnqueued = true;

      setTimeout(() => {
        const imgData = this.getExportImage();

        switch (this._exportType) {
          case ChartExportType.Base64:
            this._chartObjectToExport$.next(imgData);
            break;
          case ChartExportType.PDF:
            this.completeExportPdf(imgData);
            break;
          case ChartExportType.PNG:
            this.completeExportImage(imgData);
        }

        setTimeout(() => {
          this._loading$.next(false);
          this._enableExportChart$.next(false);
          this._exportEnqueued = false;
          this._cd.detectChanges();
        });
      }, 1000);
    }
  }

  private completeExportImage(imgData: string): void {
    setTimeout(() => {
      const fileNameExt = `${this.getFileName()}.png`;
      globalUtilsHelper.exportURL(imgData, fileNameExt);
    });
  }

  private completeExportPdf(imgData: string) {
    globalUtilsHelper.getFileSize(imgData).subscribe((imgSize) => {
      const documentItem = new PdfExportItem({
        data: imgData,
        type: 'image',
        xCoords: this._pdfImageXCoords,
      });

      const fileName = this.getFileName();

      const scaledSize = globalUtilsHelper.scaleImgHeight(imgSize, this._pdfImageWidth);

      const document = new ExportPdfDocument({
        filename: `${fileName}.pdf`,
        title: fileName,
        items: [documentItem],
        unit: 'mm',
        orientation: 'landscape',
        width: scaledSize.width,
        height: scaledSize.height,
      });

      this._genericExportService.exportPdfDocument(document);
    });
  }

  private getFileName = () => this._settings.exportFileName ?? this._genericFilename;

  private buildExportChart(): void {
    const nativeSettings = this._mapper.mapSettings(this._settings as any);
    nativeSettings.legend['type'] = 'plain';
    nativeSettings.dataZoom = [];
    nativeSettings.legend['bottom'] = '50';

    this._nativeExportSettings$.next(nativeSettings);
  }

  private getBaseExportChartOptions() {
    return {
      type: 'png',
      // Resolution ratio of exporting image, 1 by default.
      pixelRatio: 3,
      // Background color of exporting image, use backgroundColor in option by default.
      backgroundColor: '#FFFFFF',
      // Excluded components list. e.g. ['toolbox']
      excludeComponents: ['dataZoom', 'toolbox'],
    } as {
      type?: 'png' | 'jpg' | 'svg';
      pixelRatio?: number;
      backgroundColor?: ZRColor;
      connectedBackgroundColor?: ZRColor;
      excludeComponents?: string[];
    };
  }

  private getExportImage(): string {
    const opts = this.getBaseExportChartOptions();
    return this._chartInstanceExport.getConnectedDataURL(opts as any);
  }
}
